import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import InnerBox from '@repo/ui/inner-box'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import { PcrInput } from '../../../lib/types/models/pcr/pcr-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import DateChip from '../../elements/date-chip'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../elements/validation-buttons'

const formSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<Omit<PcrInput, 'createdAt' | 'updatedAt'>> = (t) =>
  z.object({
    email: createStringSchema('email', { isEmail: true }),
    firstName: createStringSchema('prénom'),
    lastName: createStringSchema('NOM'),
    phoneNumber: PHONE_NUMBER_SCHEMA(t)
  })

const PcrForm = ({ defaultValues, ...validationButtonProps }: BaseFormProps<PcrInput>) => {
  const { t } = useTranslate()
  const form = useForm<PcrInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="flex items-center gap-4">
          <DateChip date={new Date(defaultValues?.createdAt ?? new Date())} label="Créé " />
          <DateChip date={new Date(defaultValues?.updatedAt ?? new Date())} label="Mis à jour " />
        </div>
        <div className="h-4" />
        <InnerBox title="Informations personnelles">
          <div className="grid grid-cols-2 gap-2.5 w-full">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput type="text" label="Prénom" required {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput type="text" label="NOM" required {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledInput type="email" label="Email" required {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledPhoneInput
                      {...field}
                      required
                      label="Numéro de téléphone"
                      defaultCountry="FR"
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </InnerBox>
        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default PcrForm
